import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';
import ScrollToTopOnNavigate from '@/components/feature/ScrollToTopOnNavigate';
import { reputations, faqList } from '@/mocks/manga';

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const aboutJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: '关于吹雪漫画网',
    description: '吹雪漫画网是国内领先的免费无删减漫画在线阅读平台，提供下拉式流畅阅读体验，每日更新热门漫画。',
    url: 'https://chuixue.com/about',
    inLanguage: 'zh-CN',
    isPartOf: {
      '@type': 'WebSite',
      name: '吹雪漫画网',
      url: 'https://chuixue.com',
    },
  };

  const faqJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqList.map((item) => ({
      '@type': 'Question',
      name: item.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: item.answer,
      },
    })),
  };

  const breadcrumbItems = [
    { label: '首页', href: '/' },
    { label: '关于我们' },
  ];

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = (formData.get('website_alt') as string || '').trim();
    if (honeypot) {
      setFormStatus('success');
      form.reset();
      setTimeout(() => setFormStatus('idle'), 3000);
      return;
    }

    setFormStatus('loading');
    setFormError('');

    try {
      const submitAddr = 'https://readdy.ai/api/form/d9pe12hn2trhb35gvdl0';
      const response = await fetch(submitAddr, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(
          Array.from(formData.entries())
            .filter(([key]) => key !== 'website_alt')
            .map(([key, value]) => [key, value as string])
        ).toString(),
      });

      const responseText = await response.text();
      let parsed: any = {};
      try { parsed = JSON.parse(responseText); } catch (_unused) { /* ignore parse error */ }

      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        form.reset();
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;
        setFormStatus('error');
        setFormError(serverMsg || '提交失败，请稍后重试');
      }
    } catch {
      setFormStatus('error');
      setFormError('网络异常，请检查网络连接后重试');
    }
  };

  return (
    <>
      <ScrollToTopOnNavigate />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(aboutJsonLd) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(faqJsonLd) }} />
      <Navbar />
      <main className="pt-16">
        <div className="bg-background-100 border-b border-background-200/70">
          <Breadcrumb items={breadcrumbItems} />
          <div className="w-full px-4 md:px-6 pb-6">
            <div className="max-w-7xl mx-auto">
              <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900">
                关于吹雪漫画网
              </h1>
            </div>
          </div>
        </div>

        {/* Brand Introduction */}
        <section className="w-full px-4 md:px-6 py-10 md:py-14 bg-background-50">
          <div className="max-w-4xl mx-auto">
            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-4">
              品牌介绍
            </h2>
            <div className="prose prose-sm max-w-none text-foreground-600 leading-relaxed space-y-4">
              <p>
                吹雪漫画网成立于2020年，是国内领先的<strong>免费无删减漫画在线阅读平台</strong>。
                我们致力于为漫画爱好者提供优质的阅读体验，收录海量热门漫画资源，涵盖热血、恋爱、奇幻、玄幻、悬疑等多种类型。
              </p>
              <p>
                平台的命名灵感来源于「吹雪」二字所代表的纯净与浩渺——我们希望像漫天飞雪一样，将漫画的精彩与美好洒向每一位读者。
                核心作品《<strong>洞洞杂货铺</strong>》自上线以来广受好评，以其独特的奇幻世界观和温暖人心的故事情节，赢得了数百万读者的喜爱。
              </p>
              <p>
                吹雪漫画网坚持<strong>下拉式阅读</strong>的创新体验方式，让读者无需频繁翻页，只需下滑即可畅享连续阅读。
                平台所有漫画内容均保持原汁原味，<strong>不删减、不修改</strong>，确保读者能够获得最完整的漫画体验。
              </p>
              <p>
                我们拥有一支专业的编辑和运营团队，每日追踪热门漫画作品的更新动态，第一时间为读者呈现最新内容。漫画更新速度快、资源丰富、画质高清，是吹雪漫画网的核心优势。
              </p>
            </div>
          </div>
        </section>

        {/* Stats */}
        <section className="w-full px-4 md:px-6 py-10 md:py-14 bg-background-100">
          <div className="max-w-4xl mx-auto">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              {[
                { value: '10,000+', label: '漫画资源' },
                { value: '500万+', label: '注册用户' },
                { value: '99.9%', label: '服务可用率' },
                { value: '4.8', label: '用户评分' },
              ].map((stat, i) => (
                <div key={i} className="text-center">
                  <div className="font-heading text-2xl md:text-3xl font-bold text-primary-500 mb-1">
                    {stat.value}
                  </div>
                  <p className="text-sm text-foreground-500">{stat.label}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Reader Reviews */}
        <section className="w-full px-4 md:px-6 py-10 md:py-14 bg-background-50">
          <div className="max-w-4xl mx-auto">
            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-6">
              读者口碑
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {reputations.map((rep, i) => (
                <div key={i} className="p-4 rounded-lg bg-background-100 border border-background-200/70">
                  <div className="flex items-center gap-3 mb-3">
                    <img
                      src={rep.avatar}
                      alt={rep.name}
                      className="w-10 h-10 rounded-full object-cover flex-shrink-0"
                    />
                    <div>
                      <p className="text-sm font-medium text-foreground-800">{rep.name}</p>
                      <div className="flex items-center gap-0.5">
                        {Array.from({ length: 5 }, (_, j) => (
                          <i
                            key={j}
                            className={`text-xs w-3 h-3 flex items-center justify-center ${
                              j < rep.rating ? 'ri-star-fill text-primary-500' : 'ri-star-line text-foreground-300'
                            }`}
                          ></i>
                        ))}
                      </div>
                    </div>
                    <span className="ml-auto text-xs text-foreground-400 whitespace-nowrap">{rep.date}</span>
                  </div>
                  <p className="text-sm text-foreground-600 leading-relaxed">{rep.comment}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="w-full px-4 md:px-6 py-10 md:py-14 bg-background-100">
          <div className="max-w-4xl mx-auto">
            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-6">
              常见问题
            </h2>
            <div className="flex flex-col gap-3">
              {faqList.map((faq, i) => (
                <details key={i} className="group rounded-lg bg-background-50 border border-background-200/70 overflow-hidden">
                  <summary className="px-5 py-4 text-sm font-medium text-foreground-800 cursor-pointer hover:text-primary-500 transition-colors list-none flex items-center justify-between">
                    {faq.question}
                    <i className="ri-arrow-down-s-line text-base w-4 h-4 flex items-center justify-center group-open:rotate-180 transition-transform"></i>
                  </summary>
                  <div className="px-5 pb-4">
                    <p className="text-sm text-foreground-600 leading-relaxed">{faq.answer}</p>
                  </div>
                </details>
              ))}
            </div>
          </div>
        </section>

        {/* Contact Form */}
        <section className="w-full px-4 md:px-6 py-10 md:py-14 bg-background-50">
          <div className="max-w-2xl mx-auto">
            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-2 text-center">
              联系我们
            </h2>
            <p className="text-sm text-foreground-500 text-center mb-8">
              如有任何问题或建议，欢迎通过以下表单与我们联系
            </p>

            <form
              onSubmit={handleSubmit}
              data-readdy-form="true"
              className="space-y-4"
            >
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-foreground-700 mb-1">
                    姓名
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    required
                    className="w-full px-4 py-2.5 text-sm rounded-lg border border-background-300 bg-background-100 text-foreground-900 placeholder-foreground-400 focus:outline-none focus:border-primary-400 transition-colors"
                    placeholder="请输入您的姓名"
                  />
                </div>
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-foreground-700 mb-1">
                    邮箱
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    required
                    className="w-full px-4 py-2.5 text-sm rounded-lg border border-background-300 bg-background-100 text-foreground-900 placeholder-foreground-400 focus:outline-none focus:border-primary-400 transition-colors"
                    placeholder="请输入您的邮箱"
                  />
                </div>
              </div>
              <div>
                <label htmlFor="subject" className="block text-sm font-medium text-foreground-700 mb-1">
                  主题
                </label>
                <input
                  type="text"
                  id="subject"
                  name="subject"
                  required
                  className="w-full px-4 py-2.5 text-sm rounded-lg border border-background-300 bg-background-100 text-foreground-900 placeholder-foreground-400 focus:outline-none focus:border-primary-400 transition-colors"
                  placeholder="请输入主题"
                />
              </div>
              <div>
                <label htmlFor="message" className="block text-sm font-medium text-foreground-700 mb-1">
                  留言
                </label>
                <textarea
                  id="message"
                  name="message"
                  rows={5}
                  maxLength={500}
                  required
                  className="w-full px-4 py-2.5 text-sm rounded-lg border border-background-300 bg-background-100 text-foreground-900 placeholder-foreground-400 focus:outline-none focus:border-primary-400 transition-colors resize-none"
                  placeholder="请输入您的留言（500字以内）"
                ></textarea>
                <p className="text-xs text-foreground-400 mt-1">限500字符</p>
              </div>

              {/* Honeypot */}
              <div className="form-honeypot">
                <input
                  type="text"
                  name="website_alt"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                />
              </div>

              <button
                type="submit"
                disabled={formStatus === 'loading'}
                className="w-full px-6 py-3 bg-primary-500 text-background-50 font-medium rounded-full hover:bg-primary-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors whitespace-nowrap cursor-pointer text-sm"
              >
                {formStatus === 'loading' ? '提交中...' : '提交留言'}
              </button>

              {formStatus === 'success' && (
                <p className="text-sm text-accent-600 text-center font-medium">
                  留言提交成功！感谢您的反馈，我们会尽快回复。
                </p>
              )}
              {formStatus === 'error' && (
                <p className="text-sm text-red-500 text-center font-medium">
                  {formError}
                </p>
              )}
            </form>
          </div>
        </section>
      </main>
      <Footer />
      <ScrollToTop />
    </>
  );
}